<template>
  <div class="page-head-container">
    <div class="page-head clearfix">
      <div class="page-head-info clearfix">
        <div
          v-if="hasHeaderTopSlot"
          class="page-head-extra-top"
        >
          <slot name="header-top" />
        </div>
        <div v-if="!hasHeaderTopSlot">
          <a
            class="back"
            @click="navBack()"
          >
            &lt; {{ $t('back') }}
          </a>
        </div>

        <div class="row clearfix description-group">
          <div class="page-tenancy-description">
            <div class="page-tenancy-description-title">
              <span>{{ title }}</span>
              <DottedMenu
                v-if="canToggleStatus"
                class="page-tenancy-description-title-dotted-menu"
                color="darkblue"
              >
                <HKLButton
                  v-if="showInactiveButton"
                  class="page-tenancy-description-title-dotted-menu-option no-min-height"
                  @click="() => toggleTenancyStatus()"
                >
                  {{ $t('tenancy.statusToggle.inactive') }}
                </HKLButton>
                <HKLButton
                  v-else
                  class="page-tenancy-description-title-dotted-menu-option no-min-height"
                  @click="() => toggleTenancyStatus()"
                >
                  {{ $t('tenancy.statusToggle.active') }}
                </HKLButton>
              </DottedMenu>
            </div>
            <div class="clearfix tools-bar">
              <div class="float-left tools-bar__status-box">
                <HKLStatusBadge
                  v-if="currentTenancy.status"
                  class="float-left tenant-project-status pointer"
                  :value="currentTenancy.status | customStatusLabel | uppercase"
                  @click="openLifecycle"
                />
                <div
                  v-if="isEngineer"
                  class="last-update-info float-left "
                >
                  <div
                    v-if="currentTenancy.manager"
                    class="last-update float-left"
                  >
                    <div class="last-update__text">
                      {{ createdByOn }}
                    </div>
                  </div>
                  <div
                    v-if="currentTenancy.updater"
                    class="last-update float-left"
                  >
                    <div class="last-update__text">
                      {{ lastUpdatedByOn }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="buttons-slot">
                <slot />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <TenancyStatusLifecycle ref="lifecycleModal" />
  </div>
</template>

<script>
import HKLButton from '@/controls/Button/HKLButton.vue'
import DottedMenu from '@/controls/DottedMenu.vue'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { tenancyStatus as TENANCY_STATUS } from '@/utils/locale'
import { getTenancyTitle } from '@/api/tenancies/util'
import HKLStatusBadge from '@/controls/Badge/HKLStatusBadge.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { getTenancyDisplayName } from '@/modules/ccd/tenancy/utils'
import { useRouteParamId } from '@/router/useRouter'
import { useStoreActions, useStoreGetters } from '@/store'
import i18n from '@/utils/i18n'
import { computed, unref } from 'vue-demi'
import TenancyStatusLifecycle from './TenancyStatusLifecycle.vue'
import dateUtil from '@/utils/dateUtil'
import lodash from 'lodash'
import { useTenancyHistories } from './hook'

export default {
  name: 'TenancyHeader',
  components: {
    DottedMenu,
    HKLButton,
    TenancyStatusLifecycle,
    HKLStatusBadge,
  },
  setup () {
    const tenancyId = useRouteParamId()
    const lifecycleModalHook = useRefModal()
    const { currentTenancy } = useStoreGetters('ccd', ['currentTenancy'])
    const { isEngineer } = useStoreGetters('user', ['isEngineer'])
    const { updateTenancyStatus, loadTenancy } = useStoreActions('ccd', ['updateTenancyStatus', 'loadTenancy'])
    const { refresh: refreshTenancyHistories } = useTenancyHistories()
    const title = computed(() => {
      let title = `${i18n.t('tenancy.createrecord')} #`
      if (unref(tenancyId)) {
        const name = getTenancyTitle(unref(currentTenancy))
        title = `${i18n.t('tenancy.tenancyrecord')}: ${name}`
      }
      return title
    })
    const lastUpdatedByOn = computed(() => {
      const name = getTenancyDisplayName(unref(currentTenancy), unref(currentTenancy).updater)
      return i18n.t('lastUpdatedBy', [name, dateUtil.formatByLongDate(unref(currentTenancy).updatedAt, 'L LT')])
    })
    const createdByOn = computed(() => {
      const name = lodash.get(unref(currentTenancy), 'manager.displayName')
      return i18n.t('createdByOn', [name, dateUtil.formatByLongDate(unref(currentTenancy).createdAt, 'L LT')])
    })
    const canToggleStatus = computed(() => {
      const canChangeTenancyStatus = aclHelper.getAccess(ccdPermsTypes.tenancy.changeTenancyStatus)
      return unref(currentTenancy).status && canChangeTenancyStatus
    })
    const showInactiveButton = computed(() => {
      return unref(currentTenancy).status.toUpperCase() === TENANCY_STATUS.ACTIVE
    })
    return {
      isEngineer,
      createdByOn,
      lastUpdatedByOn,
      currentTenancy,
      tenancyId,
      title,
      lifecycleModal: lifecycleModalHook.modal,
      showInactiveButton,
      canToggleStatus,
      openLifecycle: () => {
        lifecycleModalHook.open()
      },
      toggleTenancyStatus: async () => {
        const nextStatus = unref(showInactiveButton) ? TENANCY_STATUS.INACTIVE : TENANCY_STATUS.ACTIVE
        await updateTenancyStatus(nextStatus)
        await Promise.all([
          loadTenancy(unref(tenancyId)),
          refreshTenancyHistories(),
        ])
      },
    }
  },
  computed: {
    /** When parent passes `header-top` (e.g. `HKLHeader` on TenancyView), skip duplicate default `< Back`. */
    hasHeaderTopSlot () {
      const s = this.$slots['header-top']
      return Array.isArray(s) && s.length > 0
    },
  },
  methods: {
    navBack () {
      this.$router.goBack()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/common';
.page-head-container {
  background: white;
  font-family: Verdana;
  color: #0c3a58;
  padding: 18px 15px 18px 15px;
  box-shadow: 0 0 4px 0 rgba(172, 172, 172, 0.5);
  .page-head {
    max-width: 1440px;
    margin: 0 auto;
  }

  .page-head-extra-top ::v-deep a.back {
    font-family: Verdana;
    font-size: 14px;
    color: #0C3A58;
  }

  .back {
    font-family: Verdana;
    padding-top: 15px;
    font-size: 14px;
    color: #0C3A58;
  }
  .tender-status {
    margin-top: 3px
  }
  .tools-bar {
    &__status-box {
      & div + div {
        margin-left: 10px
      }
    }
  }
  .buttons-slot {
    float: right;
  }
  .description-group {
    line-height: 40px;
  }
  .page-tenancy-description {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #0c2b46;
    &-title {
      display: flex;
      align-items: stretch;
      font-size: 24px;
      font-family: Georgia;
      line-height: normal;
      > span {
        display: flex;
        line-height: 40px;
        align-items: center;
      }
      &-dotted-menu {
        display: inline-block;
        &-option {
          text-wrap: nowrap;
          text-align: center !important;
          min-width: 60px;
          height: 38px;
        }
      }
    }
  }
  .page-tenancy-title-box {
    display: flex;
    flex-direction: column;
    align-items: baseline;
  }
}
.page-tenancy-description {
  &:has(&-title-dotted-menu.is-active) {
    overflow: visible;
  }
}
</style>
